//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import angular from 'angular';
import { PojoSet } from 'pojo-sets';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FullscreenModal } from '@components/FullscreenModal';
import { Alert } from '@components/atoms/alert/Alert';
import {
  FlatDefaultButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyText,
  CaptionSecondaryText,
  SubsectionText,
  SubtitleText,
  UiCopySecondaryText,
  UiCopyText,
} from '@components/atoms/text/Text';
import Popover from '@components/molecules/popover/Popover';
import { requestRightsNonMentionedModalActions } from '@galleries/actions/requestRightsNonMentionedModalActions';
import { rightsSkippedModalActions } from '@galleries/actions/rightsSkippedModalActions';
import { MediaCarousel } from '@galleries/components/content/media-carousel/MediaCarousel';
import { PendingIconOverlay } from '@galleries/components/content/pending-icon/PendingIconOverlay';
import RotatingRightsMessages from '@galleries/components/content/rotating-rights-messages/RotatingRightsMessages';
import { WorkflowBreadcrumbs } from '@galleries/components/content/workflow-breadcrumbs/WorkflowBreadcrumbs';
import { RightsSkippedModal } from '@galleries/components/modals/rights/RightsSkippedModal';
import { GalleriesTrackingIds } from '@galleries/constants/galleriesConstants';
import { getNonMentionedItems } from '@galleries/selectors/getNonMentionedItems';
import ItemId from '@models/ItemId';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalItemType } from '@models/UniversalItemType';
import makeAngularReactController from '@react-bridge/angular-react-widget';
import AnchorWidget from '@react-bridge/react-ng-widgets/anchor-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { getTikTokAccountSettings } from '@tiktok/redux/account/selectors/getTikTokAccountSettings';
import { getIsTikTokMentionedRightsEnabled } from '@uia/rights/selectors/getUIARights';
import { isDefined, isUndefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

export const RequestRightsNonMentioned = () => {
  const dispatch = useDispatch();

  const { isOpen, materializedItems, rightsPending, selectedIndex } =
    useSelector((state) => state.galleries.requestRightsNonMentionedModal);

  const nonMentionedItems = useSelector(getNonMentionedItems);

  const defaultInstagramAccount = useSelector(
    (state) => state.instagram.account.defaultInstagramAccount,
  );

  const { isConnected: isTikTokConnected } = useSelector(
    getTikTokAccountSettings,
  );

  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );

  const { clearSelectedItems } = getBulkActionsActions();

  const {
    closeModal,
    requestRightsWithOauthCheck,
    setSelectedIndexWithinBounds,
  } = requestRightsNonMentionedModalActions;

  const [showNoInstagramAccountReminder, setShowNoInstagramAccountReminder] =
    useState(false);

  const [showNoTikTokAccountReminder, setShowNoTikTokAccountReminder] =
    useState(false);

  const hasTikTokContent = useMemo(
    () =>
      isTikTokMentionedRightsEnabled &&
      nonMentionedItems[selectedIndex]?.itemType === UniversalItemType.TikTok,
    [isTikTokMentionedRightsEnabled, nonMentionedItems, selectedIndex],
  );

  const hasInstagramContent = useMemo(
    () =>
      materializedItems.some(
        (item) =>
          item.itemType === UniversalItemType.Instagram ||
          item.itemType === UniversalItemType.Facebook,
      ),
    [materializedItems],
  );

  const currentItem = nonMentionedItems[selectedIndex];

  const hasInstagramOrFacebookContent =
    currentItem &&
    (currentItem.itemType === UniversalItemType.Instagram ||
      currentItem.itemType === UniversalItemType.Facebook);

  const socialNetworkPlatform = hasTikTokContent
    ? UniversalItemType.TikTok
    : UniversalItemType.Instagram;

  const currentItemConnectionNeeds = useMemo(() => {
    const currentItem = nonMentionedItems[selectedIndex];
    if (!currentItem) {
      return { needsConnection: false, platform: null };
    }

    if (isTikTokMentionedRightsEnabled && hasTikTokContent) {
      return {
        needsConnection: !isTikTokConnected,
        platform: UniversalItemType.TikTok,
      };
    } else if (
      currentItem.itemType === UniversalItemType.Instagram ||
      currentItem.itemType === UniversalItemType.Facebook
    ) {
      return {
        needsConnection: !defaultInstagramAccount,
        platform: UniversalItemType.Instagram,
      };
    }

    return { needsConnection: false, platform: null };
  }, [
    nonMentionedItems,
    selectedIndex,
    isTikTokMentionedRightsEnabled,
    hasTikTokContent,
    isTikTokConnected,
    defaultInstagramAccount,
  ]);

  const remainingItemsSentence =
    nonMentionedItems.length === 1
      ? `You'll need to manually paste the message on the remaining item.`
      : `You'll need to manually paste the message on each of the remaining ${nonMentionedItems.length} items.`;

  const numRemainingItemsToRequest =
    nonMentionedItems.length - Object.keys(rightsPending).length;

  const onRightsSkipped = () => {
    dispatch(clearSelectedItems());
    dispatch(closeModal());
  };

  const getButtonsRow = () => {
    if (numRemainingItemsToRequest > 0) {
      const onClick = async () => {
        const currentItem = nonMentionedItems[selectedIndex];

        if (currentItemConnectionNeeds.needsConnection) {
          if (
            currentItemConnectionNeeds.platform === UniversalItemType.Instagram
          ) {
            setShowNoInstagramAccountReminder(true);
          } else if (
            currentItemConnectionNeeds.platform === UniversalItemType.TikTok
          ) {
            setShowNoTikTokAccountReminder(true);
          }
        } else if (isDefined(currentItem)) {
          await dispatch(requestRightsWithOauthCheck(currentItem));
        }
      };

      return (
        <>
          <RequestLaterButton
            onClick={() =>
              dispatch(rightsSkippedModalActions.openRightsSkippedModal())
            }
          >
            <RequestLaterButtonContent>
              Request Rights Later For&nbsp;
              {pluralize(numRemainingItemsToRequest, 'Item')}
            </RequestLaterButtonContent>
          </RequestLaterButton>
          <Popover
            content={
              <StyledBodyText>
                <MessageCopiedIcon name='circle-checkmark-filled' />
                Message Copied to Clipboard
              </StyledBodyText>
            }
            placement='top'
            shouldOpenOnHover={false}
            target={
              <RequestButton onClick={onClick}>
                <RequestButtonContent>
                  Request Rights on {socialNetworkPlatform}
                  <SmallLinkout name='linkout' />
                </RequestButtonContent>
              </RequestButton>
            }
          />
        </>
      );
    } else {
      return (
        <PrimaryButton onClick={() => dispatch(closeModal())}>
          Done
        </PrimaryButton>
      );
    }
  };

  const overlaidItems = PojoSet.toArray(rightsPending).reduce<
    Record<ItemId, true>
  >(
    (accumulator, currentValue) => ({ ...accumulator, [currentValue]: true }),
    {},
  );

  return (
    <FullscreenModal
      headerTitle={'Request Rights'}
      isActive={true}
      isOpen={isOpen}
      onClose={() => dispatch(closeModal())}
    >
      <BreadcrumbsContainer>
        <WorkflowBreadcrumbs
          currentIndex={1}
          sectionTitles={['add to galleries', 'request rights']}
        />
      </BreadcrumbsContainer>
      <HeaderContainer>
        <SubsectionText
          data-testid={
            GalleriesTrackingIds.REQUEST_RIGHTS_NON_MENTIONED_MODAL_TITLE
          }
        >
          non @mentions&nbsp;
        </SubsectionText>
        <UiCopySecondaryText>
          {nonMentionedItems.length} of{' '}
          {pluralize(materializedItems.length, 'item')}
        </UiCopySecondaryText>
      </HeaderContainer>
      <ContentContainer>
        <LeftColumn>
          {isTikTokMentionedRightsEnabled && (
            <>
              {hasTikTokContent ? (
                <LabelContainer>
                  <PlatformLabelIcon name='tiktok' />
                  <PlatformLabel>TikTok</PlatformLabel>
                </LabelContainer>
              ) : (
                hasInstagramOrFacebookContent && (
                  <LabelContainer>
                    <PlatformLabelIcon name='instagram' />
                    <PlatformLabel>Instagram</PlatformLabel>
                  </LabelContainer>
                )
              )}
            </>
          )}
          <MediaCarousel
            contentItems={materializedItems}
            imgOverlay={<PendingIconOverlay />}
            onIndexChange={(index) =>
              dispatch(setSelectedIndexWithinBounds(index))
            }
            overlaidItems={overlaidItems}
            selectedIndex={selectedIndex}
          />
        </LeftColumn>
        <RightColumn>
          <SubtitleText>Choose a Messaging Campaign</SubtitleText>
          <UiCopyText>
            <CTAContainer>
              Choose a messaging campaign to use. Click the button below to
              automatically copy your message and open the post in{' '}
              {socialNetworkPlatform}.{remainingItemsSentence}
            </CTAContainer>
          </UiCopyText>
          <StyledContainer>
            Messaging Campaign
            <SmallHelp iconSize='lg' name='help' />
          </StyledContainer>
          <StyledRotatingRightsMessages
            selectedItem={nonMentionedItems[selectedIndex]}
          />
          {!defaultInstagramAccount && hasInstagramContent && (
            <StyledAlert type='error'>
              Your Instagram account is not connected. You must be connected to
              request rights.
              <AnchorWidget
                state='account-settings.social'
                text='Click here to connect your Instagram account'
              />
            </StyledAlert>
          )}
          {hasTikTokContent && isTikTokMentionedRightsEnabled && (
            <StyledAlert type='info'>
              For TikTok content, you'll need to manually copy and paste the
              message on the TikTok platform.
            </StyledAlert>
          )}
          <ButtonRowContainer>{getButtonsRow()}</ButtonRowContainer>
          {showNoInstagramAccountReminder && (
            <ReminderContainer>
              <CaptionSecondaryText>
                <NoIgAccountReminderText>
                  You must connect your Instagram account to request rights.
                </NoIgAccountReminderText>
              </CaptionSecondaryText>
            </ReminderContainer>
          )}
          {showNoTikTokAccountReminder && isTikTokMentionedRightsEnabled && (
            <ReminderContainer>
              <CaptionSecondaryText>
                <NoIgAccountReminderText>
                  You must connect your TikTok account to request rights.
                </NoIgAccountReminderText>
              </CaptionSecondaryText>
            </ReminderContainer>
          )}
        </RightColumn>
      </ContentContainer>
      <RightsSkippedModal onConfirm={onRightsSkipped} />
    </FullscreenModal>
  );
};

const LeftColumn = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[3]};
  margin-right: ${theme.spacings[5]};
  width: 60%;
`,
);

const RightColumn = styled.div(
  ({ theme }) => `
  margin-left: ${theme.spacings[4]};
  width: 40%;
`,
);

const NoIgAccountReminderText = styled.p(
  ({ theme }) => `
  && {
    color: ${theme.colors.red8};
  }
`,
);

const CTAContainer = styled.p(
  ({ theme }) => `
  && {
    line-height: 1.5;
    margin-top: ${theme.spacings[3]};
  }
`,
);

const SmallHelp = styled(Icon)(
  ({ theme }) => `
  && {
    width: 15px;
    height: 15px;
    margin-left: ${theme.spacings[1]};
  }
`,
);

const SmallLinkout = styled(Icon)(
  ({ theme }) => `
  && {
    width: 12px;
    height: 12px;
    margin-left: ${theme.spacings[1]};
  }
`,
);

const MessageCopiedIcon = styled(Icon)(
  ({ theme }) => `
  && {
    margin-right: ${theme.spacings[2]};
    color: ${theme.colors.green6};
  }
`,
);

const StyledBodyText = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  padding: ${theme.spacings[2]};
`,
);

const BreadcrumbsContainer = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[4]};
`,
);

const HeaderContainer = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[5]};
`,
);

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledRotatingRightsMessages = styled(RotatingRightsMessages)(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]};
`,
);

const StyledAlert = styled(Alert)(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]};
`,
);

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ButtonRowContainer = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: end;
  margin-top: ${theme.spacings[2]};
  margin-bottom: ${theme.spacings[2]};
`,
);

const ReminderContainer = styled.div`
  display: flex;
  justify-content: end;
`;

const RequestLaterButton = styled(FlatDefaultButton)`
  display: flex;
  align-items: center;
  height: 100%;
`;

const RequestLaterButtonContent = styled.div`
  display: flex;
  align-items: center;
  text-overflow: unset;
  word-break: normal;
`;

const RequestButton = styled(PrimaryButton)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  height: 100%;
  margin-left: ${theme.spacings[2]};
`,
);

const RequestButtonContent = styled.div`
  display: flex;
  align-items: center;
  word-break: normal;
`;

const LabelContainer = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacings[2]};
  align-items: center;
`,
);

const PlatformLabel = styled(SubtitleText)(
  ({ theme }) => `
  padding: ${theme.spacings[1]} ${theme.spacings[2]};
  gap: ${theme.spacings[1]};
`,
);

const PlatformLabelIcon = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.black};
`,
);

angular.module('curalate').component('requestRightsNonMentioned', {
  controller: makeAngularReactController(<RequestRightsNonMentioned />),
});
